/**
 * @file NewPatientDialog — phone-first patient registration modal.
 * Markup mirrors the handoff-13 `finalForm-card` design exactly.
 *
 * Used in two places:
 *   - Pacientes page: full new-patient flow
 *   - NewAppointment page: inline create when picking a patient
 *
 * Behavior:
 *   - Phone debounced lookup (500ms after 10+ digits) -> if match, snap into
 *     "ya existe" state with locked fields; primary CTA becomes
 *     "Seleccionar paciente".
 *   - Two primary CTAs: "Guardar y agendar cita" (when showScheduleCTA=true)
 *     and "Registrar paciente". Both call onCreated; caller decides what
 *     `andSchedule` means.
 */
import React, { useEffect, useRef, useState } from "react";
import * as RadixDialog from "@radix-ui/react-dialog";
import { ChevronRight, ChevronDown, X, ArrowRight, Sparkles } from "lucide-react";
import { COUNTRY_CODES } from "../../../shared/country-codes.js";
import { Popover, PopoverContent, PopoverTrigger } from "../ui";
import { patientService } from "../services/patient";
import { toast } from "../ui";

const DEFAULT_COUNTRY = COUNTRY_CODES[0]; // +52 / MX

const formatLocalMx = (digits) => {
  // 10 digits: "55 1234 5678"
  const d = digits.replace(/\D/g, "");
  if (d.length <= 2) return d;
  if (d.length <= 6) return `${d.slice(0, 2)} ${d.slice(2)}`;
  return `${d.slice(0, 2)} ${d.slice(2, 6)} ${d.slice(6, 10)}`;
};

export default function NewPatientDialog({
  open,
  onClose,
  onCreated,
  initialPhone = "",
  showScheduleCTA = true,
}) {
  const [country, setCountry] = useState(DEFAULT_COUNTRY);
  const [countryOpen, setCountryOpen] = useState(false);
  const [phoneLocal, setPhoneLocal] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [dob, setDob] = useState("");
  const [sex, setSex] = useState(null);
  const [showOpt, setShowOpt] = useState(false);

  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupResult, setLookupResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [intent, setIntent] = useState(null);

  const lookupTimer = useRef(null);
  const phoneInputRef = useRef(null);

  // Reset state when modal opens
  useEffect(() => {
    if (!open) return;
    setCountry(DEFAULT_COUNTRY);
    setPhoneLocal(initialPhone.replace(/\D/g, "").slice(-10));
    setFirstName("");
    setLastName("");
    setEmail("");
    setDob("");
    setSex(null);
    setShowOpt(false);
    setLookupResult(null);
    setLookupLoading(false);
    setSubmitting(false);
    setIntent(null);
  }, [open, initialPhone]);

  // Phone lookup debounce
  useEffect(() => {
    if (lookupTimer.current) clearTimeout(lookupTimer.current);
    setLookupResult(null);
    const digits = phoneLocal.replace(/\D/g, "");
    if (digits.length < 10) return;
    setLookupLoading(true);
    lookupTimer.current = setTimeout(async () => {
      try {
        const e164 = `${country.code}${digits}`;
        const res = await patientService.lookupByPhone(e164);
        if (res.data) {
          // res.data is the professional's own PatientRecord (flat), or null if not in their list.
          setLookupResult(res.data);
          setFirstName(res.data.firstName || "");
          setLastName(res.data.lastName || "");
          setEmail(res.data.email || "");
        }
      } catch {
        // ignore — silent failure on lookup
      } finally {
        setLookupLoading(false);
      }
    }, 500);
    return () => lookupTimer.current && clearTimeout(lookupTimer.current);
  }, [phoneLocal, country.code]);

  const phoneDigits = phoneLocal.replace(/\D/g, "");
  const phoneValid = phoneDigits.length >= 10;
  const canSubmit = phoneValid && firstName.trim().length > 0 && !submitting;

  const handleSubmit = async (andSchedule) => {
    setIntent(andSchedule ? "schedule" : "register");
    if (lookupResult) {
      onCreated?.(lookupResult, { andSchedule });
      onClose?.();
      return;
    }
    if (!canSubmit) {
      if (!phoneValid) toast.error("Ingresa un teléfono válido (10 dígitos)");
      else if (!firstName.trim()) toast.error("Falta el nombre");
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        phone: `${country.code}${phoneDigits}`,
        firstName: firstName.trim(),
        lastName: lastName.trim() || firstName.trim(),
      };
      if (email.trim()) payload.email = email.trim();
      if (dob) payload.dateOfBirth = new Date(dob).toISOString();
      if (sex) payload.sex = sex;
      const res = await patientService.createByPro(payload);
      const patient = res.data;
      toast.success("Paciente registrado");
      onCreated?.(patient, { andSchedule });
      onClose?.();
    } catch {
      // toast handled by api interceptor
    } finally {
      setSubmitting(false);
      setIntent(null);
    }
  };

  const fieldsLocked = !!lookupResult;

  return (
    <RadixDialog.Root open={open} onOpenChange={(v) => !v && onClose?.()}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="npd-overlay" />
        <RadixDialog.Content className="npd-stage">
          <div className="npd-stage-bg" aria-hidden />
          <div className="finalForm-card npd-card" onClick={(e) => e.stopPropagation()}>
            <div className="finalForm-head">
              <div>
                <span className="eyebrow"><span className="pulse" /> Nuevo paciente</span>
                <RadixDialog.Title asChild>
                  <h1>Empieza por el <em>teléfono</em></h1>
                </RadixDialog.Title>
                <RadixDialog.Description asChild>
                  <p>Con el WhatsApp basta para crear el expediente. Lo demás puede esperar a la primera consulta.</p>
                </RadixDialog.Description>
              </div>
              <RadixDialog.Close asChild>
                <button type="button" className="finalForm-x" aria-label="Cerrar">
                  <X size={16} />
                </button>
              </RadixDialog.Close>
            </div>

            <div className="finalForm-body">
              <div>
                <div className="form-label-row">
                  <label className="form-label">Teléfono<span className="req">·</span></label>
                  <span className="form-label-sub">WhatsApp</span>
                </div>
                <div className="finalForm-phone">
                  <Popover open={countryOpen} onOpenChange={setCountryOpen}>
                    <PopoverTrigger asChild>
                      <button type="button" className="cc">
                        <span className="flag">{country.flag}</span>
                        {country.code}
                        <span className="ch"><ChevronDown size={14} /></span>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="npd-cc-pop" align="start" sideOffset={4}>
                      {COUNTRY_CODES.map((c) => (
                        <button
                          key={c.code}
                          type="button"
                          className={"npd-cc-item" + (c.code === country.code ? " active" : "")}
                          onClick={() => {
                            setCountry(c);
                            setCountryOpen(false);
                          }}
                        >
                          <span className="flag">{c.flag}</span>
                          <span className="lbl">{c.label}</span>
                          <span className="code">{c.code}</span>
                        </button>
                      ))}
                    </PopoverContent>
                  </Popover>
                  <input
                    ref={phoneInputRef}
                    autoFocus
                    placeholder="55 1234 5678"
                    inputMode="numeric"
                    value={formatLocalMx(phoneLocal)}
                    onChange={(e) => setPhoneLocal(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  />
                </div>
                <div className="finalForm-hint">
                  <Sparkles size={12} />
                  {lookupLoading
                    ? "Buscando paciente con este número…"
                    : lookupResult
                      ? `Ya existe: ${lookupResult.firstName} ${lookupResult.lastName || ""}`
                      : "Detectaremos si ya existe un paciente con este número."}
                </div>
              </div>

              <div className="finalForm-grid">
                <div>
                  <div className="form-label-row">
                    <label className="form-label">Nombre<span className="req">·</span></label>
                  </div>
                  <input
                    className="finalForm-input"
                    placeholder="Ej. Juan"
                    value={firstName}
                    disabled={fieldsLocked}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </div>
                <div>
                  <div className="form-label-row">
                    <label className="form-label">Apellido</label>
                  </div>
                  <input
                    className="finalForm-input"
                    placeholder="Ej. Ramírez"
                    value={lastName}
                    disabled={fieldsLocked}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </div>
              </div>

              {!fieldsLocked && (
                <button
                  type="button"
                  className="finalForm-toggle"
                  onClick={() => setShowOpt((v) => !v)}
                >
                  <span className="ch" style={{ transform: showOpt ? "rotate(90deg)" : "none" }}>
                    <ChevronRight size={14} />
                  </span>
                  Datos opcionales <span className="meta">correo, fecha de nacimiento, género…</span>
                </button>
              )}

              {showOpt && !fieldsLocked && (
                <div className="finalForm-opt">
                  <div>
                    <div className="form-label-row">
                      <label className="form-label">Correo electrónico</label>
                    </div>
                    <input
                      className="finalForm-input"
                      type="email"
                      placeholder="paciente@correo.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                  <div className="finalForm-grid">
                    <div>
                      <div className="form-label-row">
                        <label className="form-label">Fecha de nacimiento</label>
                      </div>
                      <input
                        className="finalForm-input"
                        type="date"
                        max={new Date().toISOString().slice(0, 10)}
                        value={dob}
                        onChange={(e) => setDob(e.target.value)}
                      />
                    </div>
                    <div>
                      <div className="form-label-row">
                        <label className="form-label">Género</label>
                      </div>
                      <div className="finalForm-segmented">
                        {[
                          { v: "F", lbl: "Femenino" },
                          { v: "M", lbl: "Masculino" },
                          { v: "OTRO", lbl: "Otro" },
                        ].map((opt) => (
                          <button
                            key={opt.v}
                            type="button"
                            className={sex === opt.v ? "active" : ""}
                            onClick={() => setSex(sex === opt.v ? null : opt.v)}
                          >
                            {opt.lbl}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="finalForm-foot">
              <button type="button" className="finalForm-btn-ghost" onClick={() => onClose?.()}>
                Cancelar
              </button>
              <div style={{ display: "flex", gap: 8 }}>
                {showScheduleCTA && (
                  <button
                    type="button"
                    className="finalForm-btn-secondary"
                    onClick={() => handleSubmit(true)}
                    disabled={submitting || (!lookupResult && !canSubmit)}
                  >
                    {submitting && intent === "schedule" ? "Guardando…" : "Guardar y agendar cita"}
                  </button>
                )}
                <button
                  type="button"
                  className="finalForm-btn-primary"
                  onClick={() => handleSubmit(false)}
                  disabled={submitting || (!lookupResult && !canSubmit)}
                >
                  {submitting && intent === "register"
                    ? "Guardando…"
                    : lookupResult
                      ? "Seleccionar paciente"
                      : "Registrar paciente"}
                  <ArrowRight size={14} />
                </button>
              </div>
            </div>
          </div>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
